import { Component, OnInit, Input, ViewChild, ElementRef, NgZone, OnDestroy } from '@angular/core';
import { UserService } from '../../user';
import { User } from '../../models';
import { SubscriptionSet, Redirection, sleep } from '../../common';
import { UsersApi, ApiSpotSSO } from '../../api';
import { environment } from '../../environment';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConfig } from '@tytapp/common';
import { CommentsService } from '../comments.service';
import * as SpotIMIntegration from './spotim-integration';
import { Shell } from '@tytapp/common/shell';
import { SpotUserProfileComponent } from './spot-user-profile.component';

let SPOT_IM_SSO_STATE = false;

@Component({
  selector: 'tyt-comments',
  templateUrl: './comments.component.html',
  styleUrls: ['./comments.component.scss']
})
export class CommentsComponent implements OnInit, OnDestroy {

  constructor(
    private ngZone : NgZone,
    private userService : UserService,
    private userApi : UsersApi,
    private router : Router,
    private route : ActivatedRoute,
    private appConfig : AppConfig,
    private redirection : Redirection,
    private commentsService : CommentsService,
    private shell : Shell
  ) { }

  private subscriptions : SubscriptionSet = new SubscriptionSet();

  get isMember() {
    if (!this.user)
      return false;
    
    return this.user.entitled;
  }

  finishingUp = false;
  readyPromise : Promise<void>;
  visible = false;
  scrollHandler : () => void;

  async ngOnInit() {
    if (typeof document === 'undefined')
      return;

    this.ready = false;

    if (typeof document !== 'undefined') {
      this.scrollHandler = () => {
        this.visible = true;
        document.removeEventListener('scroll', this.scrollHandler);
      };
      document.addEventListener('scroll', this.scrollHandler);
    }

    this.subscriptions.subscribe(this.userService.userChanged, async user => {
      console.log(`[TYT/SPOT] New TYT.com user: Waiting for Spot.im readiness...`);
      await this.readyPromise;

      console.log(`[TYT/SPOT] Syncing new TYT.com user`);
      this.user = user;
      this.syncUserState(false);
    });
    
    this.commentsService.install();

    this.setupReadyEvent();
    this.update();
  }

  private async setupReadyEvent() {

    if (typeof window === 'undefined')
      return;
    
    let loaderPromise = new Promise<void>((resolve, reject) => {
      if (window['SPOTIM'] && window['SPOTIM'].startSSO) {
        resolve();
        return;
      }

      let readyHandler : EventListener;
      
      readyHandler = () => {
        if (!window['SPOTIM'].startSSO) {
          console.error(`Spot.im fired 'spot-im-api-ready' but startSSO() is not yet available. This is a bug with Spot.im, see https://github.com/SpotIM/spotim-integration-docs/blob/master/api/single-sign-on/README.md. We will wait for startSSO() to be ready before we proceed.`);
        }

        console.log(`[TYT/SPOT] Received Spot.im API ready event.`);

        document.removeEventListener('spot-im-api-ready', readyHandler);
        resolve();
      };

      document.addEventListener('spot-im-api-ready', readyHandler, false);

      let loadCommentsInterval = null;
      loadCommentsInterval = setInterval(() => {
        if (this.isSpotLoaded || (window['SPOTIM'] && window['SPOTIM'].startSSO)) {
          clearInterval(loadCommentsInterval);
          resolve();
        }
      }, 1000);
    });

    this.readyPromise = new Promise(async (resolve, reject) => {
      await loaderPromise;
      this.isSpotLoaded = true;
      let enableIntegration = 
        await this.shell.hasFeature('apps.web.enable_community')
        && await this.shell.hasFeature('apps.web.enable_spotim_community_profiles')
      ;

      if (enableIntegration)
        SpotIMIntegration.install(user => this.showUserProfile(user));
      this.syncUserState();
    });
  }

  destroyed : boolean = false;
  
  private async showUserProfile(user : SpotIMIntegration.SpotUser) {
    this.shell.showDialog(SpotUserProfileComponent, user);
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.subscriptions.unsubscribeAll();

    if (typeof document !== 'undefined')
      document.removeEventListener('scroll', this.scrollHandler);
  }

  _identifier : string;

  @Input()
  get identifier() : string {
    return this._identifier;
  }

  set identifier(value : string) {
    this._identifier = value;
    this.update();
  }

  @Input() 
  title : string;

  @ViewChild('anchor')
  anchor : ElementRef;

  private _currentIdentifier = null;
  
  private async update() {
    this.updateSpotIm();
  }

  user : User;
  ready : boolean = true;
  loadingMessage : string = 'Getting ready...';
  attemptNumber = 0;

  private async syncUserState(allowCached = false) {
    
    if (!this.isSpotLoaded)
      return;

    if (typeof window === 'undefined')
      return;
    
    this.ready = false;
    this.loadingMessage = 'Logging you in...';
    this.finishingUp = false;

    await this.userService.ready;

    let appLoggedIn = this.user != null && this.user.entitled;
    let spotim = window['SPOTIM'];

    if (!allowCached || !window['spotimInitialSync']) {
      this.loadingMessage = 'Welcome! Just a moment...';

      if (this.attemptNumber === 1) {
        this.loadingMessage = 'Trying again...';
      } else if (this.attemptNumber === 2) {
        this.loadingMessage = 'Third time\'s a charm...';
      } else if (this.attemptNumber === 3) {
        this.loadingMessage = 'Uh oh...';

      }

      console.log(`[TYT/SPOT] Full resync`);
      window['spotimInitialSync'] = true;
      await spotim.logout();
    }

    if (!spotim) {
      this.ready = true;
      console.error(`Error: Spot.im SSO: Spot.im is not ready!`);
      return;
    }

    let timeoutTime = 10 * 1000 + this.attemptNumber * 10 * 1000;

    let timeout = setTimeout(async () => {
      if (this.ready)
        return;
      
      // Reload spot.im ?

      this.loadingMessage = `We're stuck! Reloading...`;
      await sleep(1000);

      this.attemptNumber += 1;

      this.setupReadyEvent();
      this.updateSpotIm(true);
    }, timeoutTime);

    console.log(`[TYT/SPOT] Checking app login state...`);
    if (appLoggedIn) {
      this.loadingMessage = 'Logging you in...';
      console.log(`[TYT/SPOT] App is logged in. Syncing state to Spot.im...`);
      console.dir(spotim);

      if (!spotim.startSSO) {
        console.error(`[TYT/SPOT] Error: Spot.im SSO: instance does not support SSO!`);
        this.ready = true;  
        return;
      }
  
      try {
        this.loadingMessage = 'This is fine...';
        console.log(`[TYT/SPOT] Starting Spot.im SSO...`);

        let userData = await spotim.startSSO({
          userId: this.user.uuid,
          callback: async (codeA, callback) => {
            this.loadingMessage = 'Double checking everything...';
            console.log(`[TYT/SPOT] Spot.im SSO: Step 2 (code=${codeA}):`);
            let response : ApiSpotSSO = null;

            try {
              response = await this.userApi.spotSSO({
                code: codeA
              }).toPromise();

              this.loadingMessage = 'Alright that\'s a good sign...';
              console.log(`[TYT/SPOT] Spot.im SSO: Platform returned:`);
              console.dir(response);
            } catch (e) {
              console.error(`[TYT/SPOT] Could not authenticate with Spot.im SSO:`);
              console.error(e);

              this.ready = true;
              
              return;
            }

            this.loadingMessage = 'Here we go!';
            await callback(response.code);

            this.finishingUp = true;
            await sleep(1000);

            this.ready = true;
          }
        });

        console.log(`[TYT/SPOT] Spot.im SSO completed. User data:`);
        console.dir(userData);

        // Send word that the user ID is associated now
        try {
          await this.userApi.spotAssociateUser({
            user_id: userData.id
          }).toPromise();
        } catch (e) {
          console.error(`[TYT/SPOT] Spot.im SSO: Failed to send user association to Platform:`);
          console.error(e);
        }
      } catch (e) {
        console.error(`[TYT/SPOT] Spot.im SSO: Error:`);
        console.error(e);
      }
    } else {
      try {
        await spotim.logout();
      } catch (e) {
        console.error(`Error: Spot.im SSO: Failed to log out:`);
        console.error(e);
      }
    }

    this.finishingUp = true;
    await sleep(1000);
    this.ready = true;
  }

  private isSpotLoaded : boolean = false;
  reloading = false;

  private async updateSpotIm(forceReload = false) {

    this.loadingMessage = 'Getting ready...';

    let disabled = await this.appConfig.featureEnabled('apps.web.disable_spotim_comments');

    if (disabled)
      return;

    if (typeof window === 'undefined')
      return;

    if (!forceReload && this._currentIdentifier == this._identifier)
      return;
    
    this._currentIdentifier = this._identifier;

    //await sleep(500);
    this.reloading = true;
    await sleep(1);
    this.reloading = false;
    await sleep(1);

    if (!this.anchor)
      await sleep(1000);
      
    if (!this.anchor)
      await sleep(3000);
    
    if (!this.anchor) {
      this.loadingMessage = 'Uh oh, something went wrong! Please reload the page, if the issue persists, email support@tytnetwork.com, error code: no_anchor';
      return;
    }

    this.anchor.nativeElement.innerHTML = '';

    let spotId = environment.spotId;

    if (this._identifier) {
      this.ngZone.runOutsideAngular(() => {
        let el : HTMLScriptElement = document.createElement('script');
        el.src = `https://launcher.spot.im/spot/${spotId}`;
        el.async = true;
        
        window['SPOTIM_OPTIONS'] = {
          inpageSkin: 'dark'
        };

        let url = window.location.href; // `https://tyt.com/spot/${this._identifier}`
        
        el.setAttribute('data-spotim-module', "spotim-launcher");
        el.setAttribute('data-post-url', url);
        el.setAttribute('data-article-tags', "news, politics, entertainment");
        el.setAttribute('data-post-id', this._identifier);
        el.setAttribute('data-spotim-multi-instance', 'true');
        el.setAttribute('data-inpage-skin', 'dark');
        

        console.log(`[TYT/SPOT] UPDATING SPOT.IM for URL ${window.location.toString()}, post ${this._identifier}`);
        this.anchor.nativeElement.appendChild(el);

      })
    }
  }
}
